import { z } from "zod";

export const CTA_DESTINATIONS = ["/register"] as const;

export const landingPageContentSchema = z.object({
  headline: z.string().trim().min(10).max(180),
  subheadline: z.string().trim().min(20).max(500),
  ctaText: z.string().trim().min(3).max(80),
  ctaDestination: z.string().trim().refine((value) => CTA_DESTINATIONS.includes(value as (typeof CTA_DESTINATIONS)[number]), {
    message: "CTA destination must point to the registration flow",
  }),
  reassurance: z.string().trim().min(20).max(300),
  trustSignals: z.array(z.string().trim().min(2).max(120)).min(2).max(8),
  proof: z.array(z.string().trim().min(5).max(300)).min(2).max(8),
  howItWorks: z.array(z.object({
    title: z.string().trim().min(3).max(100),
    body: z.string().trim().min(10).max(300),
  })).min(3).max(5),
  faqs: z.array(z.object({
    question: z.string().trim().min(5).max(180),
    answer: z.string().trim().min(10).max(500),
  })).min(2).max(8),
  offer: z.object({
    name: z.string().trim().min(3).max(120),
    included: z.array(z.string().trim().min(3).max(180)).min(2).max(8),
    reassurance: z.array(z.string().trim().min(3).max(100)).min(3).max(6),
    optionalNextSteps: z.string().trim().min(20).max(300),
    eligibility: z.string().trim().min(20).max(300),
  }),
});

export type LandingPageContent = z.infer<typeof landingPageContentSchema>;

export const FREE_STARTER_OFFER = {
  name: "Free Premier Pharma account + starter compliance software",
  included: [
    "Free account setup and starter DSCSA compliance software",
    "Transparent wholesale pricing after verification",
    "Real human onboarding and support",
  ],
  reassurance: ["Free account/software", "No credit card", "No contract", "No purchase required"],
  optionalNextSteps: "Ordering wholesale products and upgrading to paid ScriptTrack software are optional next steps after your account is verified.",
  eligibility: "For licensed pharmacies, clinics, medical practices, and veterinary businesses. Credentials are reviewed before account activation.",
} as const;

const COMMON_HOW_IT_WORKS = [
  { title: "Create your free account", body: "Share basic business details with no credit card, contract, or purchase required." },
  { title: "Complete a real review", body: "Submit your NPI, DEA, or state license details. Most reviews are completed within 1–2 business days." },
  { title: "See your next steps", body: "Once approved, review wholesale pricing, use your included starter software, and choose whether to order or upgrade." },
] as const;

const COMMON_FAQS = [
  { question: "Is the account really free?", answer: "Yes. Creating an account and using the included starter software does not require a credit card, contract, or purchase." },
  { question: "Do I have to place an order?", answer: "No. Wholesale ordering and paid ScriptTrack upgrades are optional after your licensed business account is approved." },
  { question: "How fast is approval?", answer: "We review licensing information carefully. Verification typically takes 1–2 business days, and we will email you when there is an update." },
  { question: "Who can create an account?", answer: "Licensed pharmacies, clinics, medical practices, and veterinary businesses can apply. We do not bypass required licensing checks." },
] as const;

const DSCSA_FAQS = [
  { question: "What does a DSCSA workflow need to cover?", answer: "A pharmacy workflow should organize transaction information, receiving review, and the records needed for follow-up. Requirements vary by business, so confirm your obligations with current FDA guidance and your compliance advisor." },
  { question: "What is included when I start?", answer: "A verified free Premier Pharma account includes starter compliance software for transaction tracking, receiving and record organization, and human setup support." },
  { question: "Do I need to buy wholesale products to use the starter software?", answer: "No. Wholesale ordering and paid ScriptTrack upgrades are optional next steps after a licensed business account is approved." },
  { question: "How does credential review work?", answer: "Licensed pharmacies, clinics, medical practices, and veterinary businesses submit business and licensing details. Credentials are reviewed before account activation, typically within 1–2 business days." },
] as const;

const SOFTWARE_FAQS = [
  { question: "What is ScriptTrack?", answer: "ScriptTrack is a web-based medication-tracking product family. A verified free account includes starter compliance software, with optional products for inventory, dispensing, prescribing, and billing workflows." },
  { question: "Which ScriptTrack product is included for free?", answer: "Starter compliance software is included with a verified free Premier Pharma account. It covers transaction tracking, receiving and record organization, and human setup support." },
  { question: "What are the optional paid products?", answer: "Atlas Inventory is $200 per month, ScriptTrack Cash is $500 per month, and ScriptTrack Full is $750 per month. Paid products are optional and eligibility is discussed after verification." },
  { question: "Who is ScriptTrack for?", answer: "Licensed pharmacies, clinics, medical practices, and veterinary businesses can apply for an account. Credential review is required before activation." },
] as const;

const UNSUPPORTED_MANAGED_CLAIMS = [
  /instant\s+approval/i,
  /approved\s+in\s+(minutes|under)/i,
  /under\s+five\s+minutes/i,
  /audit[-\s]ready/i,
  /full\s+compliance/i,
  /fully\s+compliant/i,
];

export const DEFAULT_LANDING_PAGE_CONTENT: Record<string, LandingPageContent> = {
  "drug-sourcing": {
    headline: "Your drugs. Sourced legally. Priced honestly.",
    subheadline: "Open a free account and send us what you order. Our pharmacist-led team prices each item across authorized suppliers, buys direct from the manufacturer when that's the lowest price, and reviews your DSCSA setup at no charge.",
    ctaText: "Create my free account",
    ctaDestination: "/register",
    reassurance: "No credit card. No contract. No purchase required. License verification typically takes 1–2 business days.",
    trustSignals: ["NABP VAWD accredited distributor", "LegitScript approved", "DEA licensed", "DSCSA transaction records on applicable orders"],
    proof: ["Every supplier we buy from is a licensed, authorized trading partner", "Manufacturer direct when it's cheapest, otherwise a licensed authorized distributor", "If your current supplier is cheaper on an item, we'll tell you"],
    howItWorks: [
      { title: "Create your free account", body: "Business details only. No credit card, contract, or purchase required." },
      { title: "We verify your license", body: "Submit your NPI, DEA, or state license. Most reviews are completed within 1–2 business days." },
      { title: "Send us your order list", body: "Once you're approved, share a recent invoice or list. We price each item and show the source we'd buy from." },
      { title: "Get your DSCSA review", body: "A pharmacist walks through your suppliers, transaction records, and suspect-product process with you." },
    ],
    faqs: [
      { question: "Is this really free?", answer: "Yes. The account, the sourcing quote, and the DSCSA readiness review don't require a credit card, contract, or purchase." },
      { question: "Why would a wholesaler offer a free DSCSA review?", answer: "Practices that understand traceable sourcing are the customers we want. Some compare our prices afterward. Some don't, and that's fine. The review doesn't depend on buying from us." },
      { question: "Can you beat any price?", answer: "No honest supplier can promise that. Your quote shows item by item where we're lower and where we aren't." },
      { question: "Didn't FDA extend the DSCSA deadline?", answer: "For small dispensers, FDA extended the exemption for electronic, package-level tracing to November 27, 2027. Core duties like buying only from authorized trading partners, keeping transaction records, and handling suspect product were never part of that exemption." },
    ],
    offer: {
      name: "Free account + sourcing quote + DSCSA readiness review",
      included: ["Free licensed-business account with wholesale pricing after verification", "Item-by-item sourcing quote on your current order list", "DSCSA readiness review with a pharmacist and a written gap checklist"],
      reassurance: ["Free account", "No credit card", "No contract", "No purchase required"],
      optionalNextSteps: "Ordering is optional. Order only the items where we save you money, and keep your current supplier for the rest.",
      eligibility: "For licensed pharmacies, clinics, medical practices, med spas, dental practices, surgery centers, and veterinary businesses. Credentials are reviewed before account activation.",
    },
  },
  "dscsa-compliance": {
    headline: "A clearer DSCSA workflow starts here",
    subheadline: "Organize transaction information and receiving review with included starter software, then decide what comes next after your licensed business account is verified.",
    ctaText: "Create your free account",
    ctaDestination: "/register",
    reassurance: "No credit card. No contract. No purchase required. Verification usually takes 1–2 business days.",
    trustSignals: ["NABP VAWD accredited distributor", "T2 documentation on applicable orders", "Real human support", "Licensed-business access"],
    proof: ["Organize transaction information in ScriptTrack", "Keep receiving and T2 records easier to find", "Review wholesale pricing without committing to an order"],
    howItWorks: [...COMMON_HOW_IT_WORKS],
    faqs: [...DSCSA_FAQS],
    offer: { ...FREE_STARTER_OFFER, included: [...FREE_STARTER_OFFER.included], reassurance: [...FREE_STARTER_OFFER.reassurance] },
  },
  software: {
    headline: "Medication tracking that grows with your practice",
    subheadline: "Start with included compliance workflows in ScriptTrack, then compare optional tools for inventory, dispensing, prescribing, and billing as your operation needs them.",
    ctaText: "Create your free account",
    ctaDestination: "/register",
    reassurance: "Start with a verified free account. No credit card, no contract, and no purchase required.",
    trustSignals: ["DSCSA-ready workflows", "NABP VAWD accredited distributor", "Transparent next steps", "Support from real people"],
    proof: ["Organize DSCSA transaction and receiving workflows", "Add optional inventory, dispensing, or prescribing tools", "Get setup and training support when you are ready"],
    howItWorks: [...COMMON_HOW_IT_WORKS],
    faqs: [...SOFTWARE_FAQS],
    offer: { ...FREE_STARTER_OFFER, included: [...FREE_STARTER_OFFER.included], reassurance: [...FREE_STARTER_OFFER.reassurance] },
  },
};

export function buildLandingPageFaqJsonLd(content: Pick<LandingPageContent, "faqs">) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: content.faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };
}

export const LANDING_PAGE_SEEDS = [
  { slug: "drug-sourcing", path: "/lp/drug-sourcing", label: "Drug Sourcing", liveUrl: "/lp/drug-sourcing", primaryGoal: "approved_account" },
  { slug: "dscsa-compliance", path: "/lp/dscsa-compliance", label: "DSCSA Compliance", liveUrl: "/lp/dscsa-compliance", primaryGoal: "approved_account" },
  { slug: "software", path: "/software", label: "ScriptTrack Software", liveUrl: "/software", primaryGoal: "approved_account" },
] as const;

export function validateLandingPageContent(content: unknown): { success: true; data: LandingPageContent } | { success: false; errors: string[] } {
  const result = landingPageContentSchema.safeParse(content);
  if (result.success) {
    const serialized = JSON.stringify(result.data);
    const claimErrors = UNSUPPORTED_MANAGED_CLAIMS
      .filter((pattern) => pattern.test(serialized))
      .map((pattern) => `content contains an unsupported claim pattern: ${pattern.source}`);
    if (claimErrors.length) return { success: false, errors: claimErrors };
    return result;
  }
  return { success: false, errors: result.error.issues.map((issue) => `${issue.path.join(".") || "content"}: ${issue.message}`) };
}